import { z } from 'zod';
import {
  CONSENT_VERSIONS,
  CURRENT_CONSENT_VERSION,
  DEFAULT_SECONDARY_LINK_LABEL,
  DEFAULT_SECONDARY_LINK_DESCRIPTION,
  splitConsentDetails,
  type ConsentModalSection,
} from '@sara/message-schemas';

/**
 * Per-agent consent + secondary-link form model. Each part is tri-state (the
 * secondary link can also be hidden). The form holds a `mode` plus the typed
 * `text` so switching to "custom" can seed the default text for editing. To
 * honor the never-copied invariant, `consentConfigToApi` maps custom text that
 * still equals the platform default back to `null` (`customOrNull`), so
 * "Customize → don't edit → save" keeps tracking the platform rather than
 * freezing today's wording into the row. Only genuinely edited `custom` text
 * (or `hidden`, for the link) is persisted; the API stores null/""/text
 * accordingly. Mirrors the tri-state pattern used by show-org-logo.
 */

const defaultConsent = CONSENT_VERSIONS[CURRENT_CONSENT_VERSION];

/** Platform-default consent statement text (placeholder / seed / preview). */
export const DEFAULT_CONSENT_STATEMENT = defaultConsent.label;

/**
 * Platform-default "Learn more" markdown, reassembled from the registry sections
 * as `## heading\nbody` blocks so the console preview and the seed-on-customize
 * flow show the exact shape a custom author edits.
 */
export const DEFAULT_CONSENT_DETAILS = defaultConsent.modalSections
  .map((s) => `## ${s.heading}\n${s.body}`)
  .join('\n\n');

export { DEFAULT_SECONDARY_LINK_LABEL, DEFAULT_SECONDARY_LINK_DESCRIPTION };

export const CONSENT_MODES = ['default', 'custom'] as const;
export type ConsentMode = (typeof CONSENT_MODES)[number];

export const LINK_MODES = ['default', 'custom', 'hidden'] as const;
export type LinkMode = (typeof LINK_MODES)[number];

// Kept in line with the API DTO's MaxLength decorators.
export const CONSENT_STATEMENT_MAX = 2000;
export const CONSENT_DETAILS_MAX = 10000;
export const LINK_LABEL_MAX = 120;
export const LINK_DESCRIPTION_MAX = 500;

export interface ConsentConfigFormValue {
  statementMode: ConsentMode;
  statementText: string;
  detailsMode: ConsentMode;
  detailsText: string;
  labelMode: LinkMode;
  labelText: string;
  descriptionMode: LinkMode;
  descriptionText: string;
}

/** All-default starting point for a brand-new template. */
export const DEFAULT_CONSENT_CONFIG_FORM_VALUE: ConsentConfigFormValue = {
  statementMode: 'default',
  statementText: '',
  detailsMode: 'default',
  detailsText: '',
  labelMode: 'default',
  labelText: '',
  descriptionMode: 'default',
  descriptionText: '',
};

function consentFromApi(stored: string | null): { mode: ConsentMode; text: string } {
  return stored === null ? { mode: 'default', text: '' } : { mode: 'custom', text: stored };
}

function linkFromApi(stored: string | null): { mode: LinkMode; text: string } {
  if (stored === null) return { mode: 'default', text: '' };
  if (stored === '') return { mode: 'hidden', text: '' };
  return { mode: 'custom', text: stored };
}

/** Build the form value from a version's stored (nullable) override columns. */
export function consentConfigFromApi(v: {
  consentStatement: string | null;
  consentDetails: string | null;
  secondaryLinkLabel: string | null;
  secondaryLinkDescription: string | null;
}): ConsentConfigFormValue {
  const statement = consentFromApi(v.consentStatement);
  const details = consentFromApi(v.consentDetails);
  const label = linkFromApi(v.secondaryLinkLabel);
  const description = linkFromApi(v.secondaryLinkDescription);
  return {
    statementMode: statement.mode,
    statementText: statement.text,
    detailsMode: details.mode,
    detailsText: details.text,
    labelMode: label.mode,
    labelText: label.text,
    descriptionMode: description.mode,
    descriptionText: description.text,
  };
}

/**
 * Custom text → wire value. Text that (after trimming) equals the platform
 * default maps back to `null` so "Customize → don't edit → save" keeps following
 * the platform default instead of silently freezing today's wording into the row
 * (the never-copied tri-state invariant). Empty text also maps to null.
 */
function customOrNull(text: string, platformDefault: string): string | null {
  const trimmed = text.trim();
  return trimmed === '' || trimmed === platformDefault.trim() ? null : trimmed;
}

function linkToApi(mode: LinkMode, text: string, platformDefault: string): string | null {
  if (mode === 'hidden') return ''; // "" = hidden
  if (mode === 'custom') return customOrNull(text, platformDefault);
  return null; // follow the platform default
}

/** Map the form value to the four API override fields. */
export function consentConfigToApi(v: ConsentConfigFormValue): {
  consentStatement: string | null;
  consentDetails: string | null;
  secondaryLinkLabel: string | null;
  secondaryLinkDescription: string | null;
} {
  return {
    consentStatement:
      v.statementMode === 'custom'
        ? customOrNull(v.statementText, DEFAULT_CONSENT_STATEMENT)
        : null,
    consentDetails:
      v.detailsMode === 'custom' ? customOrNull(v.detailsText, DEFAULT_CONSENT_DETAILS) : null,
    secondaryLinkLabel: linkToApi(v.labelMode, v.labelText, DEFAULT_SECONDARY_LINK_LABEL),
    secondaryLinkDescription: linkToApi(
      v.descriptionMode,
      v.descriptionText,
      DEFAULT_SECONDARY_LINK_DESCRIPTION
    ),
  };
}

/** A resolved "Learn more" section for the preview (shared shape with the API). */
export type ConsentPreviewSection = ConsentModalSection;

/**
 * Resolve what the candidate will actually see, filling in platform defaults —
 * drives the live preview. Each label/description is null when it must not render.
 */
export function resolveConsentPreview(v: ConsentConfigFormValue): {
  statement: string;
  sections: ConsentPreviewSection[];
  label: string | null;
  description: string | null;
} {
  const statement =
    v.statementMode === 'custom' && v.statementText.trim()
      ? v.statementText.trim()
      : DEFAULT_CONSENT_STATEMENT;
  const sections =
    v.detailsMode === 'custom' && v.detailsText.trim()
      ? splitConsentDetails(v.detailsText)
      : splitConsentDetails(DEFAULT_CONSENT_DETAILS);
  const label =
    v.labelMode === 'hidden'
      ? null
      : v.labelMode === 'custom'
        ? v.labelText.trim() || DEFAULT_SECONDARY_LINK_LABEL
        : DEFAULT_SECONDARY_LINK_LABEL;
  const description =
    v.descriptionMode === 'hidden'
      ? null
      : v.descriptionMode === 'custom'
        ? v.descriptionText.trim() || DEFAULT_SECONDARY_LINK_DESCRIPTION
        : DEFAULT_SECONDARY_LINK_DESCRIPTION;
  return { statement, sections, label, description };
}

/**
 * True when the platform-default "Learn more" details are in use while the
 * secondary link is hidden. The live-call promise lives specifically in the
 * default details ("Your rights" → "Request an alternative … by live call"), not
 * in the statement — so hiding the link while the details are default
 * contradicts the copy the candidate agrees to. Custom details make no such
 * promise, so a default statement alone does not warrant the warning.
 */
export function isDefaultConsentWithHiddenLink(v: ConsentConfigFormValue): boolean {
  return v.detailsMode === 'default' && v.labelMode === 'hidden';
}

const modeText = (max: number, label: string) => ({ max, label });

/**
 * Zod schema for the composite consent-config field. Custom mode requires
 * non-empty, within-limit text; other modes ignore the text. Blocks submit on
 * a custom-but-empty field (the form never disables submit — see repo rules).
 */
export const consentConfigSchema = z
  .object({
    statementMode: z.enum(CONSENT_MODES),
    statementText: z.string(),
    detailsMode: z.enum(CONSENT_MODES),
    detailsText: z.string(),
    labelMode: z.enum(LINK_MODES),
    labelText: z.string(),
    descriptionMode: z.enum(LINK_MODES),
    descriptionText: z.string(),
  })
  .superRefine((v, ctx) => {
    const checks: [boolean, string, string, { max: number; label: string }][] = [
      [
        v.statementMode === 'custom',
        v.statementText,
        'statementText',
        modeText(CONSENT_STATEMENT_MAX, 'Consent statement'),
      ],
      [
        v.detailsMode === 'custom',
        v.detailsText,
        'detailsText',
        modeText(CONSENT_DETAILS_MAX, 'Consent details'),
      ],
      [v.labelMode === 'custom', v.labelText, 'labelText', modeText(LINK_LABEL_MAX, 'Link label')],
      [
        v.descriptionMode === 'custom',
        v.descriptionText,
        'descriptionText',
        modeText(LINK_DESCRIPTION_MAX, 'Link description'),
      ],
    ];
    for (const [isCustom, text, path, { max, label }] of checks) {
      if (!isCustom) continue;
      if (text.trim().length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: `${label} is required when customized`,
          path: [path],
        });
      } else if (text.trim().length > max) {
        ctx.addIssue({
          code: 'custom',
          message: `${label} must be ${max} characters or fewer`,
          path: [path],
        });
      }
    }
  });
